import React, { useState, useRef, useEffect } from "react";
import { useSelector } from "react-redux";
import { getLogs } from "../../Services/UserService";
import {
  getMagentoSyncLogs,
  syncMagentoManualBook,
  pullMagentoProducts,
  getInitiateAllowedEmails,
  getMagentoSyncStatus,
  resetMagentoSyncLock,
  rollbackMagentoManualBook,
} from "../../Services/MagentoService";
import { authUser } from "../../Services/AuthService";
import Overlay from "../../Components/Overlay/Overlay";
import MTable from "../../Components/MTable/MTable";
import { NavLink } from "react-router-dom";
import { uuid } from "../../Utils/Utils";
import Modal from "../../Components/Layout/Modal";
import Swal from "sweetalert2";

const { $ } = window;

const LogsPage = () => {
  const { userInfo } = useSelector((state) => state.auth);
  const [activeTab, setActiveTab] = useState("user_activities");
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedMagentoItem, setSelectedMagentoItem] = useState(null);

  // Table refs
  const mTable = useRef();
  const magentoTable = useRef();

  // State for Magento Sync Per Item Code modal
  const [itemCodes, setItemCodes] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [syncingItemCode, setSyncingItemCode] = useState(false);
  const [rollingBackItemCode, setRollingBackItemCode] = useState(false);
  const [syncingAll, setSyncingAll] = useState(false);
  const [initiatingSync, setInitiatingSync] = useState(false);
  const [pullingProducts, setPullingProducts] = useState(false);
  const [rollingBack, setRollingBack] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  const [resettingLock, setResettingLock] = useState(false);

  // Allowed emails for Initiate Sync
  const [allowedInitiateEmails, setAllowedInitiateEmails] = useState(() => {
    const raw =
      process.env.REACT_APP_ALOWED_EMAIL_INITATE_MANUAL_BOOK_SYNC ||
      process.env.REACT_APP_ALLOWED_EMAIL_INITIATE_MANUAL_BOOK_SYNC ||
      process.env.ALOWED_EMAIL_INITATE_MANUAL_BOOK_SYNC ||
      "arman.septian@modena.com,ryan.ananda@modena.com";
    return raw
      .split(",")
      .map((e) => e.trim().toLowerCase())
      .filter(Boolean);
  });

  useEffect(() => {
    getInitiateAllowedEmails(
      (res) => {
        const data = res?.data || res;
        if (data?.allowed_emails && Array.isArray(data.allowed_emails)) {
          setAllowedInitiateEmails(
            data.allowed_emails.map((e) => e.trim().toLowerCase())
          );
        }
      },
      () => {
        // Fallback to initial state if API is not available
      }
    );
  }, []);

  const fetchSyncStatus = () => {
    getMagentoSyncStatus(
      (res) => {
        const data = res?.data || res;
        setSyncStatus(data);
      },
      () => {}
    );
  };

  // Poll sync status every 4 seconds to monitor live progress
  useEffect(() => {
    fetchSyncStatus();
    const interval = setInterval(() => {
      fetchSyncStatus();
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const currentUserEmail = (
    userInfo?.email ||
    (() => {
      try {
        const token = localStorage.getItem("access_token");
        if (token) {
          const payload = JSON.parse(atob(token.split(".")[1]));
          return payload?.email || "";
        }
      } catch (e) {}
      return "";
    })()
  )
    .trim()
    .toLowerCase();

  const isInitiateAllowed = Boolean(
    currentUserEmail && allowedInitiateEmails.includes(currentUserEmail)
  );

  const [state, setState] = useState({
    checked: [],
    checkAll: [],
    customFilter: "",
    processing: false,
    processing2: false,
  });

  // Columns for Tab 1: User Activities Data
  const columns = [
    {
      id: uuid(),
      title: "Data Time",
      field: "datatime",
      sortable: true,
      clickable: true,
    },
    {
      id: uuid(),
      title: "Username",
      field: "username",
      sortable: true,
      clickable: true,
    },
    {
      id: uuid(),
      title: "Activity Name",
      field: "activity_name",
      sortable: true,
      clickable: true,
    },
    {
      id: uuid(),
      title: "Action",
      style: { width: 90 },
      render: (item) => {
        return (
          <button
            type="button"
            className="btn btn-sm btn-outline-info"
            onClick={() => {
              setSelectedItem(item);
              $("#log-detail").modal("show");
            }}
          >
            <i className="fa fa-eye mr-1" /> View
          </button>
        );
      },
    },
  ];

  // Columns for Tab 2: Magento Sync Logs
  const magentoColumns = [
    {
      id: uuid(),
      title: "Executed At",
      field: "last_executed_at",
      sortable: true,
      render: (item) => item.last_executed_at || item.created_at || "-",
    },
    {
      id: uuid(),
      title: "Item Code",
      field: "item_code",
      sortable: true,
      render: (item) => <span className="font-weight-bold">{item.item_code}</span>,
    },
    {
      id: uuid(),
      title: "Language",
      field: "language_code",
      sortable: true,
      render: (item) => (
        <span className={`badge ${item.language_code === "id" ? "badge-info" : "badge-secondary"}`}>
          {item.language_code ? item.language_code.toUpperCase() : "-"}
        </span>
      ),
    },
    {
      id: uuid(),
      title: "Product Name",
      field: "product_name",
      sortable: true,
      render: (item) => item.product_name || "-",
    },
    {
      id: uuid(),
      title: "Sync Type",
      field: "sync_type",
      sortable: true,
      render: (item) => {
        const type = (
          item.sync_type ||
          item.action ||
          (item.status?.includes("ROLLBACK") ? "ROLLBACK" : "PUSH")
        ).toUpperCase();
        const isRollback = type === "ROLLBACK";
        return (
          <span className={`badge ${isRollback ? "badge-warning" : "badge-info"}`}>
            {type}
          </span>
        );
      },
    },
    {
      id: uuid(),
      title: "Manual Book",
      field: "manual_book",
      sortable: false,
      render: (item) => {
        const type = (
          item.sync_type ||
          item.action ||
          (item.status?.includes("ROLLBACK") ? "ROLLBACK" : "PUSH")
        ).toUpperCase();
        const isRollback = type === "ROLLBACK";

        // Jika sync_type adalah rollback, tampilkan link initial manualbook
        // Jika sync_type adalah push, tampilkan url lengkap manualbook sesuai product service
        const targetUrl = isRollback
          ? (item.initial_manual_book || item.manual_book_url || item.manual_book)
          : (item.manual_book_url || item.manual_book);

        if (!targetUrl) return "-";

        return (
          <a
            href={targetUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary text-truncate d-inline-block"
            style={{ maxWidth: 280 }}
            title={targetUrl}
          >
            <i className={`fa ${isRollback ? "fa-history text-warning" : "fa-file-pdf text-danger"} mr-1`} />
            {targetUrl}
          </a>
        );
      },
    },
    {
      id: uuid(),
      title: "HTTP Status",
      field: "http_status",
      sortable: true,
      render: (item) => {
        const isOk = item.http_status >= 200 && item.http_status < 300;
        return (
          <span className={`badge ${isOk ? "badge-success" : "badge-danger"}`}>
            {item.http_status || "-"}
          </span>
        );
      },
    },
    {
      id: uuid(),
      title: "Sync Web",
      field: "sync_web",
      sortable: true,
      render: (item) => {
        const type = (item.sync_type || item.action || "").toUpperCase();
        if (type === "ROLLBACK") {
          return <span className="badge badge-warning">ROLLED BACK</span>;
        }
        const isSynced = item.sync_web === true || item.status === "SUCCESS";
        return (
          <span className={`badge ${isSynced ? "badge-success" : "badge-secondary"}`}>
            {isSynced ? "TRUE" : "FALSE"}
          </span>
        );
      },
    },
    {
      id: uuid(),
      title: "Status",
      field: "status",
      sortable: true,
      render: (item) => {
        const isSuccess = item.status === "SUCCESS";
        return (
          <span className={`badge ${isSuccess ? "badge-success" : "badge-danger"}`}>
            {item.status || "-"}
          </span>
        );
      },
    },
    {
      id: uuid(),
      title: "Action",
      style: { width: 90 },
      render: (item) => (
        <button
          type="button"
          className="btn btn-sm btn-outline-info"
          onClick={() => {
            setSelectedMagentoItem(item);
            $("#magento-log-detail").modal("show");
          }}
        >
          <i className="fa fa-eye mr-1" /> View
        </button>
      ),
    },
  ];

  const onRowChecked = (checked) => {
    setState({ ...state, checked });
  };

  const { processing2, customFilter, checked, checkAll } = state;

  const propsTable = {
    columns,
    getData: getLogs,
    showIndex: true,
    showAddButton: false,
    enableFilter: true,
    orders: "datatime:desc",
    checked,
    checkAll,
    onChecked: onRowChecked,
    combineDirection: false,
    direction: "desc",
  };

  const propsMagentoTable = {
    columns: magentoColumns,
    getData: getMagentoSyncLogs,
    showIndex: true,
    showAddButton: false,
    enableFilter: true,
    orders: "last_executed_at:desc",
    combineDirection: false,
    direction: "desc",
  };

  const onCloseModal = () => {
    $("#log-detail").modal("hide");
  };

  const onCloseMagentoModal = () => {
    $("#magento-log-detail").modal("hide");
  };

  const onCloseSyncModal = () => {
    $("#sync-item-code-modal").modal("hide");
  };

  // Tag / Bubble handlers
  const handleAddItemCode = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      const code = inputValue.trim().replace(/^,+|,+$/g, "");
      if (code) {
        const splitCodes = code
          .split(",")
          .map((c) => c.trim())
          .filter(Boolean);
        const newCodes = [...itemCodes];
        splitCodes.forEach((c) => {
          if (!newCodes.includes(c)) {
            newCodes.push(c);
          }
        });
        setItemCodes(newCodes);
        setInputValue("");
      }
    }
  };

  const handleAddButton = () => {
    const code = inputValue.trim().replace(/^,+|,+$/g, "");
    if (code) {
      const splitCodes = code
        .split(",")
        .map((c) => c.trim())
        .filter(Boolean);
      const newCodes = [...itemCodes];
      splitCodes.forEach((c) => {
        if (!newCodes.includes(c)) {
          newCodes.push(c);
        }
      });
      setItemCodes(newCodes);
      setInputValue("");
    }
  };

  const handleRemoveItemCode = (codeToRemove) => {
    setItemCodes(itemCodes.filter((c) => c !== codeToRemove));
  };

  const handleClearAllItemCodes = () => {
    setItemCodes([]);
    setInputValue("");
  };

  // Reset Sync Lock Handler
  const handleResetSyncLock = () => {
    Swal.fire({
      title: "Reset Kunci Sinkronisasi?",
      text: "Gunakan fitur ini hanya jika Anda yakin proses di background mengalami freeze / nyangkut dan ingin membuka kunci agar bisa memulai sinkronisasi baru.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: '<i class="fa fa-unlock mr-1"></i> Ya, Buka Kunci',
      cancelButtonText: "Batal",
    }).then((result) => {
      if (result.isConfirmed) {
        setResettingLock(true);
        resetMagentoSyncLock(
          {},
          (res) => {
            setResettingLock(false);
            Swal.fire("Berhasil", res?.message || "Kunci sinkronisasi berhasil dibuka.", "success");
            fetchSyncStatus();
            if (magentoTable.current) {
              magentoTable.current.refresh();
            }
          },
          (err) => {
            setResettingLock(false);
            Swal.fire("Error", err?.response?.data?.message || err?.message || "Gagal membuka kunci.", "error");
          }
        );
      }
    });
  };

  // Pull Magento Products Trigger (Fetch all products from Magento website and cache in ms_magento_product)
  const handlePullMagentoProducts = () => {
    Swal.fire({
      title: "Pull Magento Products?",
      text: "This will pull all products from Magento website API and cache/save them into local database (ms_magento_product).",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#28a745",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, Pull Products",
      cancelButtonText: "Cancel",
    }).then((result) => {
      if (result.isConfirmed) {
        setPullingProducts(true);
        pullMagentoProducts(
          {},
          (res) => {
            setPullingProducts(false);
            const data = res.data || res;
            Swal.fire({
              icon: "success",
              title: "Pull Magento Products Finished",
              html: `<b>${data.message || "Magento products pulled successfully"}</b><br/>
                     Total Products: <b>${data.total ?? 0}</b>`,
            });
            if (magentoTable.current) {
              magentoTable.current.refresh();
            }
          },
          (err) => {
            setPullingProducts(false);
            Swal.fire(
              "Error",
              err?.response?.data?.message || err?.message || "Failed to pull Magento products",
              "error"
            );
          }
        );
      }
    });
  };

  // Initiate Sync Trigger (Locked with Password verification & Strict Irreversible Warning)
  const handleInitiateSync = () => {
    if (!isInitiateAllowed) {
      Swal.fire({
        icon: "warning",
        title: "Akses Dibatasi",
        text: `Fitur Initiate Sync hanya dapat dijalankan oleh user yang diotorisasi (${allowedInitiateEmails.join(", ")}).`,
      });
      return;
    }

    if (syncStatus?.is_running) {
      Swal.fire({
        icon: "info",
        title: "Proses Sedang Berjalan",
        html: `Proses sinkronisasi sedang aktif di background (<b>${syncStatus.processed_items || 0}/${syncStatus.total_items || 0}</b> produk).<br/>Mohon tunggu hingga selesai untuk mencegah tabrakan proses.`,
      });
      return;
    }

    let currentUsername = userInfo?.username || userInfo?.email || "";
    if (!currentUsername) {
      try {
        const token = localStorage.getItem("access_token");
        if (token) {
          const payload = JSON.parse(atob(token.split(".")[1]));
          currentUsername = payload?.username || payload?.email || "";
        }
      } catch (e) {}
    }

    Swal.fire({
      title: "PERINGATAN: INITIATE SYNC!",
      html: `
        <div class="text-left">
          <div class="alert alert-danger p-2 mb-3" style="font-size: 13px; line-height: 1.5;">
            <i class="fa fa-exclamation-triangle mr-1"></i>
            <b>PERINGATAN:</b><br/>
            Fungsi ini akan <b>MENGUPDATE DATA di Magento</b> yang memiliki manual book di database PMC (dibatasi <b>maksimal 100 produk</b> per kali hit dan melewati produk yang baru disinkronkan / diupdate 1 jam terakhir) dan <b>TIDAK BISA DI-ROLLBACK!</b>
          </div>
          <p class="mb-2 text-muted" style="font-size: 13px;">
            Untuk keamanan, masukkan <b>password login</b> akun Anda 
            ${currentUsername ? `(<b>${currentUsername}</b>)` : ""} untuk melanjutkan:
          </p>
        </div>
      `,
      input: "password",
      inputAttributes: {
        autocapitalize: "off",
        placeholder: "Masukkan password login Anda",
        autocomplete: "current-password",
        required: "true",
      },
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: '<i class="fa fa-lock-open mr-1"></i> Verifikasi & Jalankan Sync',
      cancelButtonText: "Batal",
      showLoaderOnConfirm: true,
      preConfirm: (password) => {
        if (!password || !password.trim()) {
          Swal.showValidationMessage("Password tidak boleh kosong!");
          return false;
        }

        return new Promise((resolve) => {
          authUser(
            { username: currentUsername, password: password.trim() },
            (res) => {
              resolve(true);
            },
            (err) => {
              const msg =
                err?.response?.data?.message ||
                err?.response?.data?.error_message ||
                "Password salah! Verifikasi gagal.";
              Swal.showValidationMessage(msg);
              resolve(false);
            }
          );
        });
      },
      allowOutsideClick: () => !Swal.isLoading(),
    }).then((result) => {
      if (result.isConfirmed && result.value) {
        setInitiatingSync(true);
        syncMagentoManualBook(
          { initiate: true },
          (res) => {
            setInitiatingSync(false);
            const data = res?.data || res || {};
            Swal.fire({
              icon: "success",
              title: data.in_background ? "Initiate Sync Berjalan di Background" : "Initiate Sync Finished",
              html: `<b>${data.message || "Initiate sync completed"}</b><br/>
                     Total Produk: <b>${data.total ?? 0}</b>
                     ${!data.in_background ? ` | Success: <span class="text-success"><b>${data.success_count ?? 0}</b></span> | Failed: <span class="text-danger"><b>${data.failed_count ?? 0}</b></span>` : ""}`,
            });
            fetchSyncStatus();
            if (magentoTable.current) {
              magentoTable.current.refresh();
            }
          },
          (err) => {
            setInitiatingSync(false);
            Swal.fire(
              "Error",
              err?.response?.data?.message || err?.message || "Failed to trigger initiate sync",
              "error"
            );
          }
        );
      }
    });
  };

  // Rollback Manual Book Trigger (Locked with Password verification & Strict Confirmation)
  const handleRollbackManualBook = () => {
    if (!isInitiateAllowed) {
      Swal.fire({
        icon: "warning",
        title: "Akses Dibatasi",
        text: `Fitur Rollback Manual Book hanya dapat dijalankan oleh user yang diotorisasi (${allowedInitiateEmails.join(", ")}).`,
      });
      return;
    }

    if (syncStatus?.is_running) {
      Swal.fire({
        icon: "info",
        title: "Proses Sedang Berjalan",
        html: `Proses sinkronisasi/rollback sedang aktif di background (<b>${syncStatus.processed_items || 0}/${syncStatus.total_items || 0}</b> produk).<br/>Mohon tunggu hingga selesai untuk mencegah tabrakan proses.`,
      });
      return;
    }

    let currentUsername = userInfo?.username || userInfo?.email || "";
    if (!currentUsername) {
      try {
        const token = localStorage.getItem("access_token");
        if (token) {
          const payload = JSON.parse(atob(token.split(".")[1]));
          currentUsername = payload?.username || payload?.email || "";
        }
      } catch (e) {}
    }

    Swal.fire({
      title: "PERINGATAN: ROLLBACK MANUAL BOOK!",
      html: `
        <div class="text-left">
          <div class="alert alert-warning p-2 mb-3" style="font-size: 13px; line-height: 1.5; border-left: 4px solid #ffc107;">
            <i class="fa fa-exclamation-triangle mr-1 text-danger"></i>
            <b>PERINGATAN:</b><br/>
            Fungsi ini akan <b>MENGEMBALIKAN DATA MANUAL BOOK DI MAGENTO</b> ke nilai awal (<code>initial_manual_book</code>) yang tersimpan di database PMC (dibatasi <b>maksimal 100 produk</b> per kali proses).
            Tindakan ini akan menimpa URL manual book di website Magento!
          </div>
          <p class="mb-2 text-muted" style="font-size: 13px;">
            Untuk keamanan, masukkan <b>password login</b> akun Anda 
            ${currentUsername ? `(<b>${currentUsername}</b>)` : ""} untuk melanjutkan:
          </p>
        </div>
      `,
      input: "password",
      inputAttributes: {
        autocapitalize: "off",
        placeholder: "Masukkan password login Anda",
        autocomplete: "current-password",
        required: "true",
      },
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: '<i class="fa fa-history mr-1"></i> Verifikasi & Jalankan Rollback',
      cancelButtonText: "Batal",
      showLoaderOnConfirm: true,
      preConfirm: (password) => {
        if (!password || !password.trim()) {
          Swal.showValidationMessage("Password tidak boleh kosong!");
          return false;
        }

        return new Promise((resolve) => {
          authUser(
            { username: currentUsername, password: password.trim() },
            (res) => {
              resolve(true);
            },
            (err) => {
              const msg =
                err?.response?.data?.message ||
                err?.response?.data?.error_message ||
                "Password salah! Verifikasi gagal.";
              Swal.showValidationMessage(msg);
              resolve(false);
            }
          );
        });
      },
      allowOutsideClick: () => !Swal.isLoading(),
    }).then((result) => {
      if (result.isConfirmed && result.value) {
        setRollingBack(true);
        rollbackMagentoManualBook(
          {},
          (res) => {
            setRollingBack(false);
            const data = res?.data || res || {};
            Swal.fire({
              icon: "success",
              title: data.in_background ? "Rollback Berjalan di Background" : "Rollback Selesai",
              html: `<b>${data.message || "Rollback completed"}</b><br/>
                     Total Produk: <b>${data.total ?? 0}</b>
                     ${!data.in_background ? ` | Success: <span class="text-success"><b>${data.success_count ?? 0}</b></span> | Failed: <span class="text-danger"><b>${data.failed_count ?? 0}</b></span>` : ""}`,
            });
            fetchSyncStatus();
            if (magentoTable.current) {
              magentoTable.current.refresh();
            }
          },
          (err) => {
            setRollingBack(false);
            Swal.fire(
              "Error",
              err?.response?.data?.message || err?.message || "Gagal menjalankan rollback",
              "error"
            );
          }
        );
      }
    });
  };

  // Sync Manual Trigger (1 jam terakhir)
  const handleSyncAll = () => {
    Swal.fire({
      title: "Sync Manual Book to Magento?",
      text: "Proses ini akan mengambil data manual book yang diperbarui dalam 1 jam terakhir dan mensinkronkannya ke Magento.",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#ffc107",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, Sync Now",
      cancelButtonText: "Cancel",
    }).then((result) => {
      if (result.isConfirmed) {
        setSyncingAll(true);
        syncMagentoManualBook(
          { hours: 1 },
          (res) => {
            setSyncingAll(false);
            const data = res?.data || res || {};
            Swal.fire({
              icon: "success",
              title: data.in_background ? "Sync Berjalan di Background" : "Sync Finished",
              html: `<b>${data.message || "Manual book sync completed"}</b><br/>
                     Total Produk: <b>${data.total ?? 0}</b>
                     ${!data.in_background ? ` | Success: <span class="text-success"><b>${data.success_count ?? 0}</b></span> | Failed: <span class="text-danger"><b>${data.failed_count ?? 0}</b></span>` : ""}`,
            });
            if (magentoTable.current) {
              magentoTable.current.refresh();
            }
          },
          (err) => {
            setSyncingAll(false);
            Swal.fire(
              "Error",
              err?.response?.data?.message || err?.message || "Failed to trigger sync all",
              "error"
            );
          }
        );
      }
    });
  };

  // Sync Per Item Code Trigger
  const handleExecuteSyncItemCodes = () => {
    if (itemCodes.length === 0) {
      Swal.fire("Warning", "Please enter at least one item code.", "warning");
      return;
    }

    setSyncingItemCode(true);
    syncMagentoManualBook(
      { item_code: itemCodes },
      (res) => {
        setSyncingItemCode(false);
        $("#sync-item-code-modal").modal("hide");
        setItemCodes([]);
        setInputValue("");
        const data = res.data || res;
        Swal.fire({
          icon: "success",
          title: "Sync Per Item Code Finished",
          html: `<b>${data.message || "Manual book sync completed"}</b><br/>
                 Total Processed: <b>${data.total ?? itemCodes.length}</b> | 
                 Success: <span class="text-success"><b>${data.success_count ?? 0}</b></span> | 
                 Failed: <span class="text-danger"><b>${data.failed_count ?? 0}</b></span>`,
        });
        if (magentoTable.current) {
          magentoTable.current.refresh();
        }
      },
      (err) => {
        setSyncingItemCode(false);
        Swal.fire(
          "Error",
          err?.response?.data?.message || err?.message || "Failed to sync item codes",
          "error"
        );
      }
    );
  };

  // Rollback Per Item Code Trigger (mengembalikan initial_manual_book untuk item code yang dipilih)
  const handleExecuteRollbackItemCodes = () => {
    if (itemCodes.length === 0) {
      Swal.fire("Warning", "Please enter at least one item code.", "warning");
      return;
    }

    if (!isInitiateAllowed) {
      Swal.fire({
        icon: "warning",
        title: "Akses Dibatasi",
        text: `Fitur Rollback Manual Book hanya dapat dijalankan oleh user yang diotorisasi (${allowedInitiateEmails.join(", ")}).`,
      });
      return;
    }

    if (syncStatus?.is_running) {
      Swal.fire({
        icon: "info",
        title: "Proses Sedang Berjalan",
        html: `Proses sinkronisasi/rollback sedang aktif di background (<b>${syncStatus.processed_items || 0}/${syncStatus.total_items || 0}</b> produk).<br/>Mohon tunggu hingga selesai untuk mencegah tabrakan proses.`,
      });
      return;
    }

    Swal.fire({
      title: `Rollback ${itemCodes.length} Item Code?`,
      html: `
        <div class="text-left">
          <div class="alert alert-warning p-2 mb-2" style="font-size: 13px; border-left: 4px solid #ffc107;">
            <i class="fa fa-exclamation-triangle mr-1 text-danger"></i>
            Tindakan ini akan <b>MENGEMBALIKAN DATA MANUAL BOOK DI MAGENTO</b> ke nilai awal (<code>initial_manual_book</code>) untuk <b>${itemCodes.length} item code</b> terpilih:
            <div class="mt-2 font-weight-bold text-dark p-2 bg-white border rounded" style="max-height: 80px; overflow-y: auto; font-size: 12px; word-break: break-all;">
              ${itemCodes.join(", ")}
            </div>
          </div>
          <p class="mb-0 text-muted" style="font-size: 13px;">Apakah Anda yakin ingin melanjutkan proses rollback?</p>
        </div>
      `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: '<i class="fa fa-history mr-1"></i> Ya, Rollback Sekarang',
      cancelButtonText: "Batal",
    }).then((result) => {
      if (result.isConfirmed) {
        setRollingBackItemCode(true);
        rollbackMagentoManualBook(
          { item_code: itemCodes },
          (res) => {
            setRollingBackItemCode(false);
            $("#sync-item-code-modal").modal("hide");
            setItemCodes([]);
            setInputValue("");
            const data = res?.data || res || {};
            Swal.fire({
              icon: "success",
              title: data.in_background ? "Rollback Berjalan di Background" : "Rollback Per Item Code Selesai",
              html: `<b>${data.message || "Rollback completed"}</b><br/>
                     Total Produk: <b>${data.total ?? itemCodes.length}</b>
                     ${!data.in_background ? ` | Success: <span class="text-success"><b>${data.success_count ?? 0}</b></span> | Failed: <span class="text-danger"><b>${data.failed_count ?? 0}</b></span>` : ""}`,
            });
            fetchSyncStatus();
            if (magentoTable.current) {
              magentoTable.current.refresh();
            }
          },
          (err) => {
            setRollingBackItemCode(false);
            Swal.fire(
              "Error",
              err?.response?.data?.message || err?.message || "Gagal melakukan rollback item code",
              "error"
            );
          }
        );
      }
    });
  };

  return (
    <div className="content-wrapper">
      <div className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-4">
              <h1 className="m-0">
                {activeTab === "user_activities" ? "User Activities Data" : "Magento Sync Logs"}
              </h1>
            </div>
            <div className="col-sm-8">
              <ol className="breadcrumb float-sm-right">
                <li className="breadcrumb-item">
                  <NavLink to="/home">Home</NavLink>
                </li>
                <li className="breadcrumb-item active">Sync & Logs</li>
              </ol>
            </div>
          </div>
        </div>
      </div>

      <section className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="col-md-12">
              {/* Tab Navigation */}
              <div className="tab mb-0" style={{ borderTopLeftRadius: "4px", borderTopRightRadius: "4px" }}>
                <p
                  className={activeTab === "user_activities" ? "tablinks active" : "tablinks"}
                  onClick={() => setActiveTab("user_activities")}
                >
                  <i className="fa fa-users mr-2" />
                  User Activities Data
                </p>
                <p
                  className={activeTab === "magento_sync" ? "tablinks active" : "tablinks"}
                  onClick={() => setActiveTab("magento_sync")}
                >
                  <i className="fa fa-sync-alt mr-2" />
                  Magento Sync Logs
                </p>
              </div>

              {/* Tab 1 Content: User Activities Data */}
              {activeTab === "user_activities" && (
                <div className="card" style={{ borderTopLeftRadius: 0 }}>
                  <Overlay display={processing2} />
                  <div className="card-body">
                    <div className="col-md-12">
                      <MTable
                        ref={mTable}
                        {...propsTable}
                        showIndex={true}
                        customFilter={customFilter}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Tab 2 Content: Magento Sync Logs */}
              {activeTab === "magento_sync" && (
                <div className="card" style={{ borderTopLeftRadius: 0 }}>
                  <Overlay display={syncingAll || initiatingSync || pullingProducts || rollingBack} />
                  <div className="card-header p-3" style={{ display: "flex", alignItems: "center" }}>
                    <h5 className="card-title font-weight-bold mb-0">
                      <i className="fa fa-book mr-2 text-warning" />
                      Magento Manual Book Sync History
                    </h5>
                    <div className="card-tools ml-auto d-flex" style={{ marginLeft: "auto", gap: "8px" }}>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-success"
                        onClick={handlePullMagentoProducts}
                        disabled={syncingAll || initiatingSync || pullingProducts}
                        title="Pull all products from Magento website and cache into local database"
                      >
                        <i className={`fa ${pullingProducts ? "fa-spinner fa-spin" : "fa-download"} mr-1`} />
                        Pull Magento Products
                      </button>
                      <button
                        type="button"
                        className={`btn btn-sm ${isInitiateAllowed ? "btn-outline-info" : "btn-secondary"}`}
                        onClick={handleInitiateSync}
                        disabled={!isInitiateAllowed || syncingAll || initiatingSync || pullingProducts || rollingBack || syncStatus?.is_running}
                        title={
                          isInitiateAllowed
                            ? "Initiate Full Sync ke Magento (Terkunci - Memerlukan password login)"
                            : `Fitur ini dinonaktifkan: Hanya diizinkan untuk email (${allowedInitiateEmails.join(", ")})`
                        }
                        style={!isInitiateAllowed ? { opacity: 0.6, cursor: "not-allowed" } : {}}
                      >
                        <i className={`fa ${initiatingSync ? "fa-spinner fa-spin" : isInitiateAllowed ? "fa-lock" : "fa-ban"} mr-1`} />
                        Initiate Sync
                      </button>
                      <button
                        type="button"
                        className={`btn btn-sm ${isInitiateAllowed ? "btn-outline-danger" : "btn-secondary"}`}
                        onClick={handleRollbackManualBook}
                        disabled={!isInitiateAllowed || syncingAll || initiatingSync || pullingProducts || rollingBack || syncStatus?.is_running}
                        title={
                          isInitiateAllowed
                            ? "Rollback Manual Book: Kembalikan URL manual book di Magento ke data awal (initial_manual_book)"
                            : `Fitur ini dinonaktifkan: Hanya diizinkan untuk email (${allowedInitiateEmails.join(", ")})`
                        }
                        style={!isInitiateAllowed ? { opacity: 0.6, cursor: "not-allowed" } : {}}
                      >
                        <i className={`fa ${rollingBack ? "fa-spinner fa-spin" : isInitiateAllowed ? "fa-history" : "fa-ban"} mr-1`} />
                        Rollback Manual Book
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-warning"
                        onClick={handleSyncAll}
                        disabled={syncingAll || initiatingSync || pullingProducts}
                        title="Sync produk yang diupdate dalam 1 jam terakhir"
                      >
                        <i className={`fa ${syncingAll ? "fa-spinner fa-spin" : "fa-redo"} mr-1`} />
                        Sync Manual (1 Jam)
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-primary"
                        onClick={() => $("#sync-item-code-modal").modal("show")}
                        disabled={syncingAll || initiatingSync || pullingProducts || syncStatus?.is_running}
                      >
                        <i className="fa fa-tags mr-1" />
                        Sync Per Item Code
                      </button>
                      {isInitiateAllowed && (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={handleResetSyncLock}
                          disabled={resettingLock}
                          title="Reset Lock: Buka paksa kunci jika proses dicurigai nyangkut"
                        >
                          <i className={`fa ${resettingLock ? "fa-spinner fa-spin" : "fa-unlock"} mr-1`} />
                          Reset Lock
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Real-time Background Sync Banner */}
                  {syncStatus?.is_running && (
                    <div
                      className="alert alert-info mx-3 mt-3 mb-0 d-flex align-items-center justify-content-between"
                      style={{ borderRadius: "8px", borderLeft: "4px solid #17a2b8" }}
                    >
                      <div>
                        <i className="fa fa-spinner fa-spin text-info mr-2" />
                        <b>Sinkronisasi sedang aktif di background:</b>{" "}
                        <span className="badge badge-light px-2 py-1 ml-1 mr-1" style={{ fontSize: "13px" }}>
                          {syncStatus.processed_items || 0} / {syncStatus.total_items || 0} produk
                        </span>
                        (Sukses: <b className="text-success">{syncStatus.success_count || 0}</b>, Gagal: <b className="text-danger">{syncStatus.failed_count || 0}</b>)
                        {syncStatus.current_item_code && (
                          <span className="ml-2 text-muted" style={{ fontSize: "12px" }}>
                            [Item aktif: <code>{syncStatus.current_item_code}</code>]
                          </span>
                        )}
                      </div>
                      <div className="d-flex" style={{ gap: "6px" }}>
                        <button
                          type="button"
                          className="btn btn-xs btn-outline-primary"
                          onClick={() => {
                            fetchSyncStatus();
                            if (magentoTable.current) magentoTable.current.refresh();
                          }}
                          title="Refresh status terkini"
                        >
                          <i className="fa fa-sync-alt mr-1" /> Refresh
                        </button>
                      </div>
                    </div>
                  )}

                  {!syncStatus?.is_running && syncStatus?.status === "CIRCUIT_BREAKER" && (
                    <div
                      className="alert alert-warning mx-3 mt-3 mb-0"
                      style={{ borderRadius: "8px", borderLeft: "4px solid #ffc107" }}
                    >
                      <i className="fa fa-exclamation-triangle text-danger mr-2" />
                      <b>Circuit Breaker Aktif:</b> {syncStatus.message}
                    </div>
                  )}

                  <div className="card-body">
                    <div className="col-md-12">
                      <MTable
                        ref={magentoTable}
                        {...propsMagentoTable}
                        showIndex={true}
                        customFilter={customFilter}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Modal 1: User Log Detail */}
      <Modal id={"log-detail"} title={"Log Details"} onClose={onCloseModal} modalSize="modal-lg">
        {selectedItem && (
          <div className="row">
            <div className="col-md-6">
              <div className="form-group">
                <label className="font-weight-bold">Activity Name</label>
                <p className="mt-n2">{selectedItem.activity_name}</p>
              </div>
              <div className="form-group">
                <label className="font-weight-bold">Module</label>
                <p className="mt-n2">{selectedItem.module}</p>
              </div>
              <div className="form-group">
                <label className="font-weight-bold">Username</label>
                <p className="mt-n2">{selectedItem.username}</p>
              </div>
              <div className="form-group">
                <label className="font-weight-bold">Resource</label>
                <p className="mt-n2">{selectedItem.resource}</p>
              </div>
              <div className="form-group">
                <label className="font-weight-bold">IP Address</label>
                <p className="mt-n2">{selectedItem.ip_address}</p>
              </div>
              <div className="form-group">
                <label className="font-weight-bold">Status</label>
                <p className="mt-n2">{selectedItem.http_status}</p>
              </div>
              <div className="form-group">
                <label className="font-weight-bold">Method</label>
                <p className="mt-n2">{selectedItem.rest_method}</p>
              </div>
            </div>
            <div className="col-md-6">
              <div className="form-group">
                <label className="font-weight-bold">Data Time</label>
                <p className="mt-n2">{selectedItem.datatime}</p>
              </div>
              <div className="form-group">
                <label className="font-weight-bold">Previous Value</label>
                <p className="mt-n2">
                  <pre style={{ whiteSpace: "pre-wrap", wordWrap: "break-word" }}>
                    {selectedItem.previous_value || "-"}
                  </pre>
                </p>
              </div>
              <div className="form-group">
                <label className="font-weight-bold">Next Value</label>
                <p className="mt-n2">
                  <pre style={{ whiteSpace: "pre-wrap", wordWrap: "break-word" }}>
                    {selectedItem.new_value || "-"}
                  </pre>
                </p>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal 2: Magento Log Detail */}
      <Modal
        id={"magento-log-detail"}
        title={"Magento Sync Log Detail"}
        onClose={onCloseMagentoModal}
        modalSize="modal-lg"
      >
        {selectedMagentoItem && (
          <div className="row">
            <div className="col-md-6">
              <div className="form-group">
                <label className="text-muted small font-weight-bold">ITEM CODE</label>
                <h5 className="font-weight-bold">{selectedMagentoItem.item_code}</h5>
              </div>
              <div className="form-group">
                <label className="text-muted small font-weight-bold">PRODUCT NAME</label>
                <p className="mb-0 font-weight-bold">{selectedMagentoItem.product_name || "-"}</p>
              </div>
              <div className="form-group">
                <label className="text-muted small font-weight-bold">SYNC TYPE</label>
                <div>
                  <span
                    className={`badge ${
                      (selectedMagentoItem.sync_type || selectedMagentoItem.action) === "ROLLBACK"
                        ? "badge-warning"
                        : "badge-info"
                    }`}
                  >
                    {selectedMagentoItem.sync_type || selectedMagentoItem.action || "PUSH"}
                  </span>
                </div>
              </div>
              <div className="form-group">
                <label className="text-muted small font-weight-bold">LANGUAGE</label>
                <div>
                  <span
                    className={`badge ${
                      selectedMagentoItem.language_code === "id" ? "badge-info" : "badge-secondary"
                    }`}
                  >
                    {selectedMagentoItem.language_code
                      ? selectedMagentoItem.language_code.toUpperCase()
                      : "-"}
                  </span>
                </div>
              </div>
              <div className="form-group">
                <label className="text-muted small font-weight-bold">MANUAL BOOK (PRODUCT SERVICE)</label>
                <p className="mb-1 text-break">{selectedMagentoItem.manual_book || "-"}</p>
                {selectedMagentoItem.manual_book_url && (
                  <a
                    href={selectedMagentoItem.manual_book_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="small text-primary font-weight-bold"
                  >
                    <i className="fa fa-external-link-alt mr-1" /> Open File URL
                  </a>
                )}
              </div>
              <div className="form-group">
                <label className="text-muted small font-weight-bold">INITIAL MANUAL BOOK (MAGENTO)</label>
                <p className="mb-0 small text-break">
                  {selectedMagentoItem.initial_manual_book ? (
                    <a
                      href={selectedMagentoItem.initial_manual_book}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-info font-weight-bold"
                    >
                      <i className="fa fa-external-link-alt mr-1" />
                      {selectedMagentoItem.initial_manual_book}
                    </a>
                  ) : "-"}
                </p>
              </div>
              <div className="form-group">
                <label className="text-muted small font-weight-bold">PREVIOUS / OLD MANUAL BOOK</label>
                <p className="mb-0 small text-break">
                  {selectedMagentoItem.old_manual_book ? (
                    <a
                      href={selectedMagentoItem.old_manual_book}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-warning font-weight-bold"
                    >
                      <i className="fa fa-external-link-alt mr-1" />
                      {selectedMagentoItem.old_manual_book}
                    </a>
                  ) : "-"}
                </p>
              </div>
            </div>
            <div className="col-md-6">
              <div className="form-group">
                <label className="text-muted small font-weight-bold">STATUS & SYNC WEB</label>
                <div>
                  <span
                    className={`badge mr-2 px-2 py-1 ${
                      selectedMagentoItem.status === "SUCCESS" ? "badge-success" : "badge-danger"
                    }`}
                  >
                    {selectedMagentoItem.status}
                  </span>
                  <span
                    className={`badge mr-2 px-2 py-1 ${
                      selectedMagentoItem.sync_web === true || selectedMagentoItem.status === "SUCCESS"
                        ? "badge-success"
                        : "badge-secondary"
                    }`}
                  >
                    SYNC WEB: {selectedMagentoItem.sync_web === true || selectedMagentoItem.status === "SUCCESS" ? "TRUE" : "FALSE"}
                  </span>
                  <span className="badge badge-light border px-2 py-1">
                    HTTP {selectedMagentoItem.http_status}
                  </span>
                </div>
              </div>
              <div className="form-group">
                <label className="text-muted small font-weight-bold">LAST SYNC WEB</label>
                <p className="mb-0">
                  {selectedMagentoItem.last_sync ? (
                    <span className="text-success font-weight-bold">
                      <i className="fa fa-clock mr-1" />
                      {selectedMagentoItem.last_sync}
                    </span>
                  ) : "-"}
                </p>
              </div>
              <div className="form-group">
                <label className="text-muted small font-weight-bold">LAST EXECUTED AT</label>
                <p className="mb-0">
                  {selectedMagentoItem.last_executed_at || selectedMagentoItem.created_at || "-"}
                </p>
              </div>
              <div className="form-group">
                <label className="text-muted small font-weight-bold">MAGENTO ENDPOINT</label>
                <p className="mb-0 small text-monospace text-break bg-light p-2 rounded border">
                  {selectedMagentoItem.endpoint || "-"}
                </p>
              </div>
            </div>

            <div className="col-12 mt-3">
              <div className="form-group">
                <label className="text-muted small font-weight-bold">REQUEST PAYLOAD</label>
                <pre
                  className="bg-dark text-light p-3 rounded small"
                  style={{ maxHeight: "200px", overflow: "auto" }}
                >
                  {JSON.stringify(selectedMagentoItem.payload, null, 2)}
                </pre>
              </div>
              <div className="form-group">
                <label className="text-muted small font-weight-bold">MAGENTO RESPONSE</label>
                <pre
                  className="bg-dark text-light p-3 rounded small"
                  style={{ maxHeight: "200px", overflow: "auto" }}
                >
                  {typeof selectedMagentoItem.response === "object"
                    ? JSON.stringify(selectedMagentoItem.response, null, 2)
                    : selectedMagentoItem.response || "-"}
                </pre>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal 3: Sync Per Item Code with Bubble/Chips */}
      <Modal
        id={"sync-item-code-modal"}
        title={"Sync Manual Book per Item Code"}
        onClose={onCloseSyncModal}
        modalSize="modal-md"
      >
        <div>
          <div className="form-group">
            <label className="font-weight-bold">Item Codes</label>
            <div className="input-group">
              <input
                type="text"
                className="form-control"
                placeholder="Type item code and press Enter (e.g. BH 1725)..."
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleAddItemCode}
                disabled={syncingItemCode || rollingBackItemCode}
              />
              <div className="input-group-append">
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  onClick={handleAddButton}
                  disabled={syncingItemCode || rollingBackItemCode || !inputValue.trim()}
                >
                  <i className="fa fa-plus mr-1" /> Add
                </button>
              </div>
            </div>
            <small className="text-muted">
              Press <b>Enter</b> or comma to add multiple item codes.
            </small>
          </div>

          {/* Chips / Bubbles Container */}
          <div className="form-group">
            <div className="d-flex justify-content-between align-items-center mb-1">
              <label className="small text-muted mb-0">
                Selected Item Codes ({itemCodes.length})
              </label>
              {itemCodes.length > 0 && (
                <button
                  type="button"
                  className="btn btn-link btn-sm text-danger p-0"
                  onClick={handleClearAllItemCodes}
                  disabled={syncingItemCode || rollingBackItemCode}
                >
                  <i className="fa fa-trash-alt mr-1" /> Clear All
                </button>
              )}
            </div>
            <div
              className="p-2 bg-light border rounded d-flex flex-wrap align-items-center"
              style={{ minHeight: "80px", maxHeight: "180px", overflowY: "auto", gap: "6px" }}
            >
              {itemCodes.length === 0 ? (
                <span className="text-muted small w-100 text-center py-3">
                  <i className="fa fa-info-circle mr-1" />
                  No item codes added yet. Type above and press Enter.
                </span>
              ) : (
                itemCodes.map((code) => (
                  <span
                    key={code}
                    className="badge badge-warning px-2 py-1 d-inline-flex align-items-center shadow-sm"
                    style={{ fontSize: "13px", fontWeight: "600", borderRadius: "14px" }}
                  >
                    <span>{code}</span>
                    <button
                      type="button"
                      className="close ml-2 text-dark"
                      style={{ fontSize: "15px", lineHeight: "1", opacity: 0.7 }}
                      onClick={() => handleRemoveItemCode(code)}
                      disabled={syncingItemCode || rollingBackItemCode}
                      title="Remove"
                    >
                      &times;
                    </button>
                  </span>
                ))
              )}
            </div>
          </div>

          {/* Action buttons */}
          <div className="d-flex justify-content-between align-items-center mt-4">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onCloseSyncModal}
              disabled={syncingItemCode || rollingBackItemCode}
            >
              Cancel
            </button>
            <div className="d-flex" style={{ gap: "8px" }}>
              <button
                type="button"
                className="btn btn-outline-danger"
                onClick={handleExecuteRollbackItemCodes}
                disabled={syncingItemCode || rollingBackItemCode || itemCodes.length === 0}
                title="Rollback manual book ke initial_manual_book untuk item code terpilih"
              >
                {rollingBackItemCode ? (
                  <>
                    <i className="fa fa-spinner fa-spin mr-1" /> Rolling Back...
                  </>
                ) : (
                  <>
                    <i className="fa fa-history mr-1" /> Rollback ({itemCodes.length})
                  </>
                )}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleExecuteSyncItemCodes}
                disabled={syncingItemCode || rollingBackItemCode || itemCodes.length === 0}
              >
                {syncingItemCode ? (
                  <>
                    <i className="fa fa-spinner fa-spin mr-1" /> Syncing...
                  </>
                ) : (
                  <>
                    <i className="fa fa-cloud-upload-alt mr-1" /> Start Sync Now ({itemCodes.length})
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default LogsPage;
